import { Icon } from "@vericus/cadmus-icons";
import {
  DateTimePicker,
  NumberInput,
  Radio,
  Spacer,
  Switch,
  Text,
} from "@vericus/cadmus-ui";

import { startOfMinute } from "date-fns";

import { FadeIn } from "@/components/fade-in";
import {
  AssessmentType,
  LtiService,
  MarkingToolPdf,
  TaskFormat,
} from "@/generated/graphql";
import { useFeatureFlagsFragment } from "@/graphql/institution-selectors";
import { HeraLaunch, useHeraLaunch } from "@/utils/useHeraLaunch";

import { RestrictStudentView } from "../../../../task/components/restrict-student-view";
import { useSettingsFormContext } from "../../../hooks/use-individual-settings-form";
import { MarkingToolRadio } from "../assignment-settings/marking-tool-radio";
import { SettingInfoCard } from "../setting-info-card";
import * as styles from "../styles.css";
import { StyledLinkButton } from "./shared";

export interface ExamMarkingSettingProps {
  hasCanvasAGS?: boolean;
  ltiServices?: LtiService[];
  assessmentId?: string;
  hasSubmissions?: boolean;
  format: TaskFormat;
}

/**
 * Allow teachers to change these marking settings:
 * - Marking tool
 * - Total points
 * - Weighting
 * - Anonymous marking
 * - Feedback return date
 */
export const ExamMarkingSetting = ({
  hasCanvasAGS,
  ltiServices = [],
  assessmentId,
  hasSubmissions,
  format,
}: ExamMarkingSettingProps) => {
  const settingsForm = useSettingsFormContext();
  const {
    examFeedbackDate,
    noExamFeedbackDate,
    anonymousMarking,
    noWeight,
    gradingService,
    maxGrade,
    weight,
    restrictMarkingToolPdf,
    markingToolPdf,
  } = settingsForm.values;

  const rubricDisplayDisabled =
    typeof settingsForm.values.rubricDisplayDisabled == "boolean"
      ? settingsForm.values.rubricDisplayDisabled
      : settingsForm.values.assessmentType === AssessmentType.Exam;

  const onHeraLaunch = useHeraLaunch({
    launch: HeraLaunch.Settings,
    assessmentId,
  });

  const features = useFeatureFlagsFragment();

  const isWeightingExpanded = !noWeight;
  const isAnonymouslyMarked = anonymousMarking ?? false;
  const feedbackReturnDate =
    examFeedbackDate != null
      ? startOfMinute(new Date(examFeedbackDate))
      : undefined;

  return (
    <div className={styles.settingContainer}>
      <Text kind="headingOne">Marking</Text>
      <MarkingToolRadio
        gradingService={gradingService}
        setGradingService={(gradingService) => {
          settingsForm.setFieldValue("gradingService", gradingService);
        }}
        hasSpeedGrader={hasCanvasAGS}
        hasMoodleGrader={features?.moodleGrader === true}
        ltiServices={ltiServices}
        hasTurnitinGrader={
          features?.turnitinGrader === true && features?.turnitin === true
        }
        hasCadmusGrader={features?.cadmusGrader === true}
        format={format}
      />
      <Spacer spacing={36} />
      {(gradingService === "turnitin" || gradingService === "cadmusgrader") && (
        <>
          <Text kind="headingFour" as="div">
            Total points
          </Text>
          <Text kind="bodyMd" as="div">
            The exam will be marked out of{" "}
            <NumberInput
              decimalScale={0}
              allowNegative={false}
              aria-label="total points in the exam"
              aria-invalid={!!settingsForm.errors.maxGrade}
              disabled={format === TaskFormat.Multiformat}
              isAllowed={({ floatValue }) => {
                if (floatValue === undefined) return true;
                return floatValue >= 0 && floatValue <= 1000;
              }}
              value={`${maxGrade}`}
              onValueChange={({ floatValue }) => {
                settingsForm.setFieldValue("maxGrade", floatValue ?? 0);
                settingsForm.validateField("maxGrade");
              }}
              errorMessage={settingsForm.errors.maxGrade}
            />{" "}
            points
          </Text>
          <Spacer spacing={60} />
        </>
      )}

      <Switch
        size="md"
        label="Weighting"
        labelPosition="left"
        labelKind="headingFour"
        className={styles.sectionRow}
        checked={isWeightingExpanded}
        onCheckedChange={() =>
          settingsForm.setFieldValue("noWeight", !noWeight)
        }
      />
      <Spacer spacing={16} />
      <SettingInfoCard backgroundColor="blue" showNewPill={false}>
        Inform students of how much the exam is worth. This does not affect how
        grades are returned to the Learning Management System (e.g. Grade
        Centre)
      </SettingInfoCard>
      {isWeightingExpanded && (
        <FadeIn>
          <Spacer spacing={16} />
          <Text kind="bodyMd" as="div">
            This exam is worth{" "}
            <NumberInput
              size="sm"
              decimalScale={0}
              allowNegative={false}
              aria-label="Exam weighting (percentage of the unit)"
              style={{ width: 100 }}
              isAllowed={({ floatValue }) => {
                if (typeof floatValue === "number") {
                  return floatValue >= 0 && floatValue <= 100;
                }
                return true;
              }}
              suffix="%"
              value={weight ?? undefined}
              onValueChange={({ floatValue }) => {
                settingsForm.setFieldValue("weight", floatValue ?? null);
              }}
            />{" "}
            of the unit
          </Text>
        </FadeIn>
      )}

      <Spacer spacing={60} />

      <AnonymousMarking
        hasSubmissions={hasSubmissions ?? false}
        isAnonymouslyMarked={isAnonymouslyMarked}
        setAnonymousMarking={(anonymousMarking) =>
          settingsForm.setFieldValue("anonymousMarking", anonymousMarking)
        }
        gradingService={gradingService}
      />
      <Spacer spacing={60} />

      {gradingService === "cadmusgrader" && (
        <>
          <Switch
            label="Show rubrics to students"
            labelPosition="left"
            labelKind="headingFour"
            className={styles.sectionRow}
            checked={!rubricDisplayDisabled}
            onCheckedChange={() =>
              settingsForm.setFieldValue(
                "rubricDisplayDisabled",
                !rubricDisplayDisabled
              )
            }
          />
          <Text kind="bodyMd" color="shade1">
            Students can see all grading rubrics while writing their responses.
          </Text>
          <Spacer spacing={36} />
        </>
      )}

      {format === TaskFormat.Multiformat &&
        ["speedgrader", "moodlegrader"].includes(gradingService) && (
          <>
            <RestrictStudentView
              restrictMarkingToolPdf={restrictMarkingToolPdf}
              setRestrictStudentResultView={(value: boolean) =>
                settingsForm.setFieldValue("restrictMarkingToolPdf", value)
              }
              markingToolPdf={markingToolPdf}
              setStudentResultView={(value: MarkingToolPdf) =>
                settingsForm.setFieldValue("markingToolPdf", value)
              }
            />
            <Spacer spacing={36} />
          </>
        )}

      <Spacer spacing={60} />
      <StyledLinkButton
        icon={<Icon iconName="Turnitin" />}
        onClick={onHeraLaunch}
      >
        View advanced Turnitin settings
      </StyledLinkButton>

      <Spacer spacing={60} />

      {gradingService !== "cadmusgrader" && (
        <>
          <Switch
            label="Written feedback return"
            labelPosition="left"
            className={styles.sectionRow}
            labelKind="headingFour"
            checked={!noExamFeedbackDate}
            onCheckedChange={() => {
              if (!noExamFeedbackDate) {
                settingsForm.setFieldValue("examFeedbackDate", null);
              }
              settingsForm.setFieldValue(
                "noExamFeedbackDate",
                !noExamFeedbackDate
              );
            }}
          />

          {!noExamFeedbackDate ? (
            <FadeIn>
              <Text kind="bodyMd" as="span">
                Students can access written feedback and grades via Cadmus after{" "}
              </Text>
              <DateTimePicker
                width={250}
                style={{ display: "inline-block" }}
                timePickerProps={{ format: "12h" }}
                valueFormat="DD MMM YYYY hh:mm A"
                aria-label="Feedback return date"
                onChange={(date) =>
                  settingsForm.setFieldValue("examFeedbackDate", date)
                }
                onBlur={() => settingsForm.validateField("examFeedbackDate")}
                value={feedbackReturnDate}
                errorMessage={settingsForm.errors.examFeedbackDate}
              />
            </FadeIn>
          ) : (
            <Text kind="bodyMd">
              Only grades will be returned to the LMS, students will not be
              accessing written feedback via Cadmus.
            </Text>
          )}
        </>
      )}
    </div>
  );
};

interface AnonymousMarkingProps {
  hasSubmissions: boolean;
  isAnonymouslyMarked: boolean;
  setAnonymousMarking: (anonymous: boolean | null) => void;
  gradingService: string;
}

export const AnonymousMarking = (props: AnonymousMarkingProps) => {
  const { hasSubmissions, setAnonymousMarking, isAnonymouslyMarked } = props;

  // Addendum on passing anonymous marking setting to Turnitin
  const feedbackStudio =
    props.gradingService === "turnitin" ? " and Feedback Studio" : "";

  return (
    <>
      <Text kind="headingFour">Anonymous Marking</Text>
      <Spacer spacing={16} />
      <SettingInfoCard backgroundColor="blue" showNewPill={false}>
        Hide student names on submissions with anonymous marking. Anonymity
        settings cannot be changed once students have submitted.
      </SettingInfoCard>
      <Spacer spacing={16} />
      <div role="radiogroup" aria-label="Anonymous marking">
        <Radio
          disabled={hasSubmissions}
          colorVariant="lilac500"
          checked={!isAnonymouslyMarked}
          content={`Show student names in the class list${feedbackStudio}`}
          onChange={() => setAnonymousMarking(false)}
        />
        <Radio
          disabled={hasSubmissions}
          colorVariant="lilac500"
          checked={isAnonymouslyMarked}
          content={`Hide student names for anonymity in the class list${feedbackStudio}`}
          onChange={() => setAnonymousMarking(true)}
        />
      </div>
      {props.gradingService === "speedgrader" && (
        <>
          <Spacer spacing={18} />
          <a
            href="https://support.cadmus.io/setting-requirements-canvas#marking-feedback"
            target="_blank"
            rel="noopener noreferrer"
          >
            <StyledLinkButton kind="solid">
              {`Remember to ${
                props.isAnonymouslyMarked ? "hide" : "show"
              } student names in SpeedGrader `}
              <Icon iconName="Right" />
            </StyledLinkButton>
          </a>
        </>
      )}
    </>
  );
};
